// oidc.js — how the panel finds the cluster's sign-in provider, and the client it signs in as.
//
// The panel is a public OpenID Connect client of the cluster's sign-in provider. This module holds what
// the sign-in package cannot know:
//
//   • WHERE the provider is. A surface asks the origin that served it, which names its provider at
//     `/.well-known/oauth-protected-resource`. A surface served by something that answers nothing — a
//     static host — asks the entry node, or the node address it is given.
//   • WHO this surface is. Its client id is its own origin's host, with `-<port>` when the origin
//     names one — derived here from where the page was loaded, so a surface is told nothing and a
//     panel on a static host signs in through a member that never served it.
//
// The panel's session is `@thekrystalship/auth-client`'s (`sessionStore.js`).

// Normalize to an http(s) origin with no trailing slash. "" if unusable.
function originOf(input) {
  const s = String(input || "").trim();
  if (!s) return "";
  try { return new URL(/^https?:\/\//i.test(s) ? s : "https://" + s).origin; } catch { return ""; }
}

// The client id an origin signs in as: its host, lowercased, and `-<port>` when the origin names a
// port its scheme would not imply. Null for anything that is not an http(s) origin, or whose host is
// an IPv6 literal, which no client id can spell.
function clientIdFor(origin) {
  let url;
  try { url = new URL(origin); } catch { return null; }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = url.hostname.toLowerCase();
  if (!host || host.includes(":") || host.startsWith("[")) return null;
  return url.port ? host + "-" + url.port : host;
}

// The issuer named by a protected-resource document, or "" when it names none usable.
function issuerIn(doc) {
  const servers = doc && Array.isArray(doc.authorization_servers) ? doc.authorization_servers : [];
  const issuer = typeof servers[0] === "string" ? servers[0].trim().replace(/\/+$/, "") : "";
  return originOf(issuer) ? issuer : "";
}

// Which provider signs sessions for whatever answers at `address`.
//
//   { ok: true,  issuer, via }                the provider, and the address that named it
//   { ok: false, reason: "no_provider", via } a node answered and has no issuer configured
//   { ok: false, reason: "unreachable" }      nothing answered as a node
//   { ok: false, reason: "invalid" }          not an address
async function discoverProvider(address, { fetchImpl = fetch, signal } = {}) {
  const base = originOf(address);
  if (!base) return { ok: false, reason: "invalid" };
  const read = async (path) => {
    try {
      const res = await fetchImpl(base + path, { headers: { Accept: "application/json" }, signal });
      return { status: res.status, body: res.ok ? await res.json().catch(() => null) : null };
    } catch { return { status: 0, body: null }; }
  };

  const resource = await read("/.well-known/oauth-protected-resource");
  if (resource.status === 200) {
    const issuer = issuerIn(resource.body);
    if (issuer) return { ok: true, issuer, via: base };
  }
  if (resource.status === 503) return { ok: false, reason: "no_provider", via: base };
  return { ok: false, reason: "unreachable" };
}

export { clientIdFor, discoverProvider, originOf };
